import React from "react";
import styled from "styled-components";

import Text from "@ingka/text";
import Skeleton from "@ingka/skeleton";
import { MenuItem } from "@ingka/list-box";

import { tertials } from "../../utils/constants";
import { SrOnly } from "../styles";
import { fontSize500 } from "@ingka/variables/design-tokens";
import { PillDropdown } from "../Dropdown";

interface DashboardTileProps {
	activeMarket: string | null;
	activeTertial: string;
	updateCount: number | undefined;
	updateCounterTileLoading: boolean;
	updateCounterOnlyLoading: boolean;
	setActiveTertial: (data: string) => void;
}

const StyledTile = styled.div`
	margin-left: 1rem;
	width: fit-content;
	background-color: #e3f5ff;
	padding: 0rem 1.2rem 1.2rem 1.2rem;
	border-radius: 1rem;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	height: 6rem;
`;

const StyledSkeletonCounter = styled(Skeleton)`
	margin-top: 0.5rem;
	width: 16rem;
`;

const StyledTileHeader = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 1rem;
	height: 2.5rem;
`;

const TertialListWrapper = styled.div`
	padding-top: 1.2rem;
`;

const StyledSkeleton = styled(Skeleton)`
	width: 16rem;
	border-radius: 1rem;
	margin-left: 1rem;
`;

const StyledHeaderText = styled(Text)`
	display: flex;
	flex-direction: column;
	height: 2.5rem;
	padding-top: 1.2rem;
	.update_count {
		font-size: ${fontSize500};
	}
`;

const Layout: React.FC<DashboardTileProps> = ({
	activeTertial,
	setActiveTertial,
	updateCount,
	updateCounterTileLoading,
	updateCounterOnlyLoading,
}) => {
	const [announcement, setAnnouncement] = React.useState<string | null>(null);

	const announce = (msg: string) => {
		setAnnouncement(msg);
		setTimeout(() => setAnnouncement(null), 800);
	};

	const announcementMessage = React.useMemo(() => {
		if (updateCounterOnlyLoading) {
			return announce("Fetching number of updates!");
		}
		if (updateCount) {
			return announce(`Number of updates in ${activeTertial} is ${updateCount}`);
		}
		return announce(`No updates for ${activeTertial}`);
	}, [updateCounterOnlyLoading, updateCount, activeTertial]);

	const label = "Number of updates";

	if (updateCounterTileLoading) {
		return <StyledSkeleton width="20rem" height="5.5rem" />;
	}

	return (
		<StyledTile>
			<StyledTileHeader>
				<SrOnly aria-live="polite">{announcement}</SrOnly>
				<StyledHeaderText textStyle="Heading.XS">
					{label}
					{!updateCounterOnlyLoading ? (
						<span className="update_count" aria-live="polite">
							{updateCount}
						</span>
					) : (
						<StyledSkeletonCounter width="2rem" height="2rem" />
					)}
				</StyledHeaderText>

				<TertialListWrapper>
					<PillDropdown
						id="applications-dropdown"
						value={activeTertial}
						onChange={(value: string) => setActiveTertial(value)}
						ariaLabel={label}
						label={activeTertial}
					>
						{tertials?.map((tertial) => (
							<MenuItem key={tertial} title={tertial} id={tertial} />
						))}
					</PillDropdown>
				</TertialListWrapper>
			</StyledTileHeader>
		</StyledTile>
	);
};

export default Layout;
